import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FaTimes } from 'react-icons/fa';
import { calculateAgeFromDob, normalizeRelationshipForApi, findMobileSubgroupDuplicate, capitalizeRelationship } from '../../utils/subscriberHelpers';

const MALE_RELATIONS = ['father', 'son', 'brother', 'father_in_law'];
const FEMALE_RELATIONS = ['mother', 'daughter', 'sister', 'mother_in_law'];

function getSelfGender(existingMembers) {
  const self = existingMembers.find(m =>
    (m.relation || m.relationship || '').toLowerCase() === 'self'
  );
  if (!self) return '';
  const g = (self.gender || '').toLowerCase();
  if (g === 'male') return 'Male';
  if (g === 'female') return 'Female';
  return '';
}

function getGenderOptions(relation, selfGender) {
  const rel = (relation || '').toLowerCase();
  if (MALE_RELATIONS.includes(rel)) return ['Male', 'Other'];
  if (FEMALE_RELATIONS.includes(rel)) return ['Female', 'Other'];
  if (rel === 'spouse') {
    if (selfGender === 'Male') return ['Female', 'Other'];
    if (selfGender === 'Female') return ['Male', 'Other'];
    return ['Male', 'Female', 'Other'];
  }
  return ['Male', 'Female', 'Other'];
}

function getAutoGender(relation, selfGender) {
  const rel = (relation || '').toLowerCase();
  if (MALE_RELATIONS.includes(rel)) return 'Male';
  if (FEMALE_RELATIONS.includes(rel)) return 'Female';
  if (rel === 'spouse') {
    if (selfGender === 'Male') return 'Female';
    if (selfGender === 'Female') return 'Male';
    return '';
  }
  return '';
}

const AddFamilyMemberModal = ({ mounted, show, onClose, onSubmit, existingSingletonRelations, existingMembers = [], overlayClassName = 'bg-black/80', size = 'sm', initialData = null, apiErrors = {} }) => {
  const isEditing = !!initialData;
  const [formData, setFormData] = useState({
    name: '',
    relationship: '',
    dob: '',
    age: '',
    gender: '',
    mobile: '',
    email: ''
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (show) {
      setFormData(initialData ? (() => {
        const dob = initialData.dob || initialData.date_of_birth || '';
        const calculatedAge = calculateAgeFromDob(dob);
        return {
          name: initialData.name || '',
          relationship: normalizeRelationshipForApi(initialData.relation || ''),
          dob,
          age: calculatedAge !== null ? calculatedAge : (initialData.age || ''),
          gender: initialData.gender || '',
          mobile: initialData.contact_number || initialData.phone || initialData.mobile || '',
          email: initialData.email || '',
        };
      })() : {
        name: '',
        relationship: '',
        dob: '',
        age: '',
        gender: '',
        mobile: '',
        email: ''
      });
      setErrors({});
    }
  }, [show, initialData]);

  const selfGender = getSelfGender(existingMembers);

  const handleChange = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      if (field === 'dob') {
        const calculatedAge = calculateAgeFromDob(value);
        updated.age = calculatedAge !== null && calculatedAge !== undefined ? calculatedAge : '';
      }
      if (field === 'relationship' && !isEditing) {
        updated.gender = getAutoGender(value, selfGender);
      }
      return updated;
    });
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const isGenderLocked = isEditing &&
    normalizeRelationshipForApi(initialData?.relation || '') === 'self' &&
    !!(initialData?.gender);

  const validate = () => {
    const newErrors = {};
    if (!formData.name || formData.name.trim() === '') newErrors.name = 'Name is required';
    if (!formData.relationship) newErrors.relationship = 'Relationship is required';
    if (!formData.dob) newErrors.dob = 'Date of birth is required';
    else if (formData.age !== '' && Number(formData.age) < 1) newErrors.dob = 'Age must be at least 1 year';
    if (!formData.gender) newErrors.gender = 'Gender is required';
    // AP-827: mobile is required. Doctor's VC Accept/Reject WhatsApp uses the
    // patient mobile as a template parameter — a member without a number
    // silently breaks the booking downstream. Blocking here is the cheapest
    // place to catch it; backend also guards in assign_doctor_to_application.
    if (!formData.mobile || !formData.mobile.trim()) {
      newErrors.mobile = 'Mobile number is required (used for consultation WhatsApp)';
    } else {
      const cleaned = formData.mobile.replace(/\D/g, '');
      if (cleaned.length !== 10) {
        newErrors.mobile = 'Contact number must be exactly 10 digits';
      } else if (formData.relationship) {
        const conflict = findMobileSubgroupDuplicate(formData.mobile, formData.relationship, existingMembers);
        if (conflict) {
          newErrors.mobile = `This number is already used by ${conflict.name ? `${conflict.name} (${capitalizeRelationship(conflict.relationship || conflict.relation)})` : capitalizeRelationship(conflict.relationship || conflict.relation)}`;
        }
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validate()) {
      onSubmit(formData);
    }
  };

  if (!mounted || !show) return null;

  return createPortal(
    <div className={`fixed top-0 left-0 w-screen h-screen z-[9999] m-0 p-0 flex items-center justify-center ${overlayClassName}`}>
      <div className={`bg-white rounded-lg border border-gray-200 w-full shadow-xl m-6 flex flex-col max-h-[90vh] ${size === 'md' ? 'max-w-md' : 'max-w-sm'}`}>
        <div className="px-4 py-3 border-b flex items-center justify-between shrink-0">
          <div>
            <h2 className="text-base font-semibold text-gray-900">{initialData ? 'Edit Member' : 'Add Family Member'}</h2>
            <p className="text-xs text-gray-500 mt-1">{initialData ? `Editing details for ${initialData.name || 'member'}` : 'Add a new family member to subscription'}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <FaTimes />
          </button>
        </div>
        <div className="p-4 space-y-3 overflow-y-auto">
          {Object.keys(apiErrors).length > 0 && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 flex items-start gap-2">
              <FaTimes className="text-red-400 shrink-0 mt-0.5" size={11} />
              <div className="space-y-0.5">
                {Object.entries(apiErrors).map(([field, msgs]) => (
                  <p key={field} className="text-xs text-red-600">
                    <span className="font-medium capitalize">{field.replace(/_/g, ' ')}: </span>
                    {Array.isArray(msgs) ? msgs.join(' ') : String(msgs)}
                  </p>
                ))}
              </div>
            </div>
          )}
          {errors.api && <div className="text-sm text-red-600">{errors.api}</div>}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              className={`w-full border rounded-md px-2 py-1.5 text-sm bg-gray-50 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.name ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Enter full name"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
            />
            {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
          </div>
          
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Relationship <span className="text-red-500">*</span>
            </label>
            <select
              className={`w-full border rounded-md px-2 py-1.5 text-sm bg-gray-50 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.relationship ? 'border-red-500' : 'border-gray-300'
              }`}
              value={formData.relationship}
              onChange={(e) => handleChange('relationship', e.target.value)}
            >
              <option value="">Select relationship</option>
              <option value="self" disabled={existingSingletonRelations.has('self')}>
                {existingSingletonRelations.has('self') ? 'Self (Already added)' : 'Self'}
              </option>
              <option value="father" disabled={existingSingletonRelations.has('father')}>
                {existingSingletonRelations.has('father') ? 'Father (Already added)' : 'Father'}
              </option>
              <option value="mother" disabled={existingSingletonRelations.has('mother')}>
                {existingSingletonRelations.has('mother') ? 'Mother (Already added)' : 'Mother'}
              </option>
              <option value="son">Son</option>
              <option value="spouse" disabled={existingSingletonRelations.has('spouse')}>
                {existingSingletonRelations.has('spouse') ? 'Spouse (Already added)' : 'Spouse'}
              </option>
              <option value="daughter">Daughter</option>
              <option value="brother">Brother</option>
              <option value="sister">Sister</option>
              <option value="mother_in_law" disabled={existingSingletonRelations.has('mother_in_law')}>
                {existingSingletonRelations.has('mother_in_law') ? 'Mother-in-law (Already added)' : 'Mother-in-law'}
              </option>
              <option value="father_in_law" disabled={existingSingletonRelations.has('father_in_law')}>
                {existingSingletonRelations.has('father_in_law') ? 'Father-in-law (Already added)' : 'Father-in-law'}
                  </option>
            </select>
            {errors.relationship && <p className="text-xs text-red-500 mt-1">{errors.relationship}</p>}
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Date of Birth <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                className={`w-full border rounded-md px-2 py-1.5 text-sm bg-gray-50 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.dob ? 'border-red-500' : 'border-gray-300'
                }`}
                value={formData.dob}
                onChange={(e) => handleChange('dob', e.target.value)}
              />
              {errors.dob && <p className="text-xs text-red-500 mt-1">{errors.dob}</p>}
            </div>
            
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Age</label>
              <input
                type="number"
                className="w-full border rounded-md px-2 py-1.5 text-sm bg-gray-100 text-gray-900 focus:ring-0 focus:border-gray-300 cursor-not-allowed"
                value={formData.age !== null && formData.age !== undefined ? formData.age : ''}
                readOnly
                disabled
              />
            </div>
          </div>
          
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Gender <span className="text-red-500">*</span>
            </label>
            <select
              className={`w-full border rounded-md px-2 py-1.5 text-sm text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                isGenderLocked ? 'bg-gray-100 cursor-not-allowed border-gray-300' : 'bg-gray-50 border-gray-300'
              } ${errors.gender ? 'border-red-500' : ''}`}
              value={formData.gender}
              onChange={(e) => handleChange('gender', e.target.value)}
              disabled={isGenderLocked}
            >
              <option value="">Select gender</option>
              {getGenderOptions(formData.relationship, selfGender).map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
            {errors.gender && <p className="text-xs text-red-500 mt-1">{errors.gender}</p>}
          </div>
          
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Mobile <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              className={`w-full border rounded-md px-2 py-1.5 text-sm bg-gray-50 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.mobile ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="9999999999"
              maxLength={10}
              value={formData.mobile}
              onKeyDown={(e) => {
                if ([8, 9, 27, 13, 46, 35, 36, 37, 38, 39, 40].indexOf(e.keyCode) !== -1 ||
                    (e.keyCode === 65 && e.ctrlKey === true) ||
                    (e.keyCode === 67 && e.ctrlKey === true) ||
                    (e.keyCode === 86 && e.ctrlKey === true) ||
                    (e.keyCode === 88 && e.ctrlKey === true)) {
                  return;
                }
                if ((e.shiftKey || (e.keyCode < 48 || e.keyCode > 57)) &&
                    (e.keyCode < 96 || e.keyCode > 105) &&
                    ![43, 45, 32, 40, 41].includes(e.keyCode)) {
                  e.preventDefault();
                }
              }}
              onChange={(e) => {
                const filteredValue = e.target.value.replace(/[^0-9+\-()\s]/g, '');
                handleChange('mobile', filteredValue);
                const cleaned = filteredValue.replace(/\D/g, '');
                if (filteredValue.trim() === '') {
                  setErrors(prev => ({ ...prev, mobile: '' }));
                } else if (cleaned.length !== 10) {
                  setErrors(prev => ({ ...prev, mobile: 'Contact number must be exactly 10 digits' }));
                } else if (formData.relationship) {
                  const conflict = findMobileSubgroupDuplicate(filteredValue, formData.relationship, existingMembers);
                  if (conflict) {
                    setErrors(prev => ({ ...prev, mobile: `This number is already used by ${conflict.name ? `${conflict.name} (${capitalizeRelationship(conflict.relationship || conflict.relation)})` : capitalizeRelationship(conflict.relationship || conflict.relation)}` }));
                  } else {
                    setErrors(prev => ({ ...prev, mobile: '' }));
                  }
                } else {
                  setErrors(prev => ({ ...prev, mobile: '' }));
                }
              }}
            />
            {errors.mobile && <p className="text-xs text-red-500 mt-1">{errors.mobile}</p>}
          </div>
          
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              className={`w-full border rounded-md px-2 py-1.5 text-sm bg-gray-50 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.email ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="email@example.com"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
            />
            {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
          </div>
          
          <div className="flex justify-end space-x-2 pt-3">
            <button
              onClick={onClose}
              className="px-3 py-1.5 border border-gray-300 rounded-md text-xs text-gray-700 hover:text-white hover:bg-blue-600 hover:border-blue-600 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              className="px-3 py-1.5 bg-blue-600 text-white rounded-md text-xs hover:bg-blue-700"
            >
              {initialData ? 'Save Changes' : 'Submit'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AddFamilyMemberModal;
